import * as React from 'react';
import { cn } from '@seller-hermes/utils/cn';

export type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  startAddornment?: string | any;
  endAddornment?: string | any;
  error?: boolean;
};

// Utility function to merge multiple refs into a single ref
function mergeRefs<T>(
  ...refs: (
    | React.MutableRefObject<T | null>
    | ((instance: T | null) => void)
    | null
  )[]
) {
  const filteredRefs = refs.filter(Boolean); // Filter out null refs
  if (filteredRefs.length === 0) return null;
  if (filteredRefs.length === 1) return filteredRefs[0];

  return (node: T | null) => {
    for (const ref of filteredRefs) {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    }
  };
}
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      disabled,
      startAddornment,
      endAddornment,
      error,
      ...props
    },
    ref,
  ) => {
    const inputRef = React.useRef<HTMLInputElement | null>(null);

    const handleStartAdornmentClick = () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    };

    // Merge the inputRef with the forwarded ref
    const mergedRef = mergeRefs(inputRef, ref);

    return (
      <div
        className={cn(
          'border-input hover:border-ring/30 group relative flex h-10 items-center rounded-md border',
          error &&
            'border-destructive ring-destructive/30 hover:border-destructive ring',
        )}
      >
        {startAddornment && (
          <div
            className={cn(
              'text-md text-muted-foreground flex h-full select-none justify-center items-center rounded-l-md px-3 whitespace-nowrap shrink-0',
              disabled ? 'cursor-not-allowed' : 'cursor-text',
            )}
            onClick={handleStartAdornmentClick}
          >
            {startAddornment}
          </div>
        )}
        <input
          type={type}
          className={cn(
            'border-input placeholder:text-muted-foreground h-full w-full shrink rounded-sm bg-transparent px-3 py-1 text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium',
            'disabled:bg-muted disabled:cursor-not-allowed disabled:opacity-50',
            'focus-visible:border-ring focus-visible:ring-ring/30 focus-visible:outline-none focus-visible:ring',
            startAddornment && 'rounded-l-none border-l-0',
            endAddornment && 'rounded-r-none border-r-0',
            type === 'time' &&
              '[&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-clear-button]:hidden [&::-webkit-datetime-edit-fields-wrapper]:p-0 [&::-webkit-inner-spin-button]:hidden',
            className,
          )}
          ref={mergedRef}
          disabled={disabled}
          {...props}
        />
        {endAddornment && (
          <div
            className={cn(
              'text-md text-muted-foreground flex h-full select-none items-center rounded-r-md',
              disabled ? 'cursor-not-allowed' : 'cursor-text',
            )}
            onClick={handleStartAdornmentClick}
          >
            {endAddornment}
          </div>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';

export default Input;
